import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import { useSessionStore } from './session'

import { IUserAccountInfoDTO } from '@/types/userAccount'
import { AccountApi, DEBUGC, DEBUG_WARN } from '@/util'

interface IGridCellSize
{
    cellSize: number
    cellSizeSmall: number
}

/** store of currently active user */
export const useUserStore = defineStore('userStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _hasLoaded: Ref<boolean> = ref(false)
    const _userData: Ref<IUserAccountInfoDTO | null> = ref(null)
    const _language: Ref<string> = ref('K')
    const _loginType: Ref<'none' | 'device' | 'user'> = ref('none')
    const _adminCurrency: Ref<string|null> = ref(null)

    const state = { _hasLoaded, _userData, _language, _loginType, _adminCurrency }

    /********************************************************************************************************************
     GETTERS
     ********************************************************************************************************************/
    const language = computed(() => _language.value)
    const userData = computed(() => _userData.value)
    const currency = computed(() => {
        if (loginType.value == 'device') {
            return 'EUR' // needed for device logins
        }

        if (isAdmin.value && _adminCurrency.value != null) {
            return _adminCurrency.value
        }

        return userData.value?.partnerAccount?.currency
    })

    const userCurrencyHasLoaded = computed(() => currency.value != null)

    const permissions = computed(() => _userData.value?.permissions)
    const isAdmin = computed(() => permissions.value?.isAdmin === true)
    const loginType = computed(() => _loginType.value)
    const hasPartnerMaterials = computed(() => _userData.value?.hasPartnerMaterials === true)

    /** The current user's own identity. Always available at user login — never derive it from partnerAccount.userAccounts */
    const currentUserAccount = computed(() => _userData.value?.currentUserAccount ?? null)

    /** Display name of the current user, falling back to the email address. Null when no user is logged in. */
    const currentUserFullName = computed(() => {
        const account = currentUserAccount.value

        if (account == null) {
            return null
        }

        const name = `${account.firstName} ${account.lastName}`.trim()

        return name.length > 0 ? name : account.email
    })

    /** Email address (username) of the current user. Null when no user is logged in. */
    const currentUserEmail = computed(() => currentUserAccount.value?.email ?? null)

    /** True when the current user is allowed to see the other user accounts of the partner */
    const mayViewPartnerUsers = computed(() => (_userData.value?.partnerAccount?.userAccounts?.length ?? 0) > 0)

    const getters = { language, userData, currency, userCurrencyHasLoaded, permissions, isAdmin, loginType, hasPartnerMaterials,
        currentUserAccount, currentUserEmail, currentUserFullName, mayViewPartnerUsers }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    /** Sets the currently active language */
    function setLanguage(language: string) {
        _language.value = language
        localStorage.setItem('language', language)
    }

    /** Sets the currently active currency (only allowed in admin sessions) */
    function setAdminCurrency(currency: string) {
        _adminCurrency.value = currency
    }

    /**
     * Sets the cell size for the spare part result grid
     * @param cellSize Cell size for normal screen resolution
     * @param cellSizeSmall Cell Size for small screen resolution
     */
    function setGridCellSize(cellSizes: IGridCellSize) {
        localStorage.setItem('gridCellSize', cellSizes.cellSize.toString())
        localStorage.setItem('gridCellSizeSmall', cellSizes.cellSizeSmall.toString())
    }

    function getGridCellSize(): IGridCellSize {
        const cellSize = localStorage.getItem('gridCellSize')
        const cellSizeSmall = localStorage.getItem('gridCellSizeSmall')

        return {
            cellSize: cellSize ? parseInt(cellSize) : 200,
            cellSizeSmall: cellSizeSmall ? parseInt(cellSizeSmall) : 2
        }
    }

    function refresh() {
        DEBUGC('REFRESHING USER STORE', 'orange')
        reset()

        const sessionStore = useSessionStore()
        const isPartnerLogin = sessionStore.isPartnerLogin()

        if (isPartnerLogin) {
                sessionStore.isAuthenticated()
                    .then(isAuthenticated => {
                        if (isAuthenticated) {
                            const api = new AccountApi
                            api.getUserAccountInfo()
                                .then(response => {
                                    _loginType.value = 'user'
                                    _userData.value = response.data
                                    _hasLoaded.value = true
                                })
                                .catch(error => DEBUG_WARN('UserStore: Failed to load user account info:', error))
                        }
                    })
                    .catch(error => DEBUG_WARN('UserStore: Failed to check authentication:', error))
            }
            else {
                sessionStore.isAuthenticated()
                    .then(isAuthenticated => {
                        if (isAuthenticated) {
                            _loginType.value = 'device'
                        }
                    })
                    .catch(error => DEBUG_WARN('UserStore: Failed to check authentication:', error))
            }
    }

    function reset() {
        _loginType.value = 'none'
        _userData.value = null
    }

    const actions = { setLanguage, setAdminCurrency, refresh, reset, setGridCellSize, getGridCellSize }

    /********************************************************************************************************************
     INITIALIZATION
     ********************************************************************************************************************/
    function defaultLanguage(): string {
        const browserDefault = navigator.language.split('-')

        if (browserDefault.length) {
            switch (browserDefault[0].toLowerCase()) {
                case 'da': return 'K'
                case 'sv': return 'V'
                case 'de': return 'D'
                default: return 'E'
            }
        }

        return 'E'
    }

    // Ensure initial refresh
    if (!_hasLoaded.value) {
        _language.value = localStorage.getItem('language') ?? defaultLanguage()
        refresh()
    }

    /********************************************************************************************************************
     INTERFACE
     ********************************************************************************************************************/
    return { ...state, ...getters, ...actions }
})